import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
] as const;

interface LanguageSwitcherProps {
  variant?: "landing" | "sidebar" | "compact";
}

function buildTargetUrl(lang: string): string {
  const path = window.location.pathname;
  const rel = BASE && path.startsWith(BASE) ? path.slice(BASE.length) || "/" : path;
  let stripped = rel;
  if (rel === "/es") stripped = "/";
  else if (rel.startsWith("/es/")) stripped = rel.slice(3);

  let target: string;
  if (lang === "es") {
    target = stripped === "/" ? `${BASE}/es/` : `${BASE}/es${stripped}`;
  } else {
    target = `${BASE}${stripped === "/" ? "/" : stripped}`;
  }

  // Drop ?lang= from the search since the path is now the source of truth.
  const search = new URLSearchParams(window.location.search);
  search.delete("lang");
  const searchStr = search.toString();
  return target + (searchStr ? `?${searchStr}` : "") + window.location.hash;
}

export function LanguageSwitcher({ variant = "landing" }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const switchLanguage = async (lang: string) => {
    if (lang === currentLang) {
      setOpen(false);
      return;
    }
    localStorage.setItem("viralens_lang", lang);
    setOpen(false);

    // Best-effort: persist user locale to backend before navigating.
    try {
      await fetch(`${BASE}/api/me/locale`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locale: lang }),
      });
    } catch {
      // Ignore network errors — anonymous visitors and offline users still
      // need the language to switch via the URL.
    }

    // Navigating triggers a fresh load so the prerendered HTML for the
    // target language is delivered to crawlers and users alike.
    window.location.assign(buildTargetUrl(lang));
  };

  const currentLabel = LANGUAGES.find((l) => l.code === currentLang)?.label ?? "English";
  const currentCode = currentLang.toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        title={currentLabel}
        className={cn(
          "flex items-center gap-1 transition-all",
          variant === "sidebar" && "px-3 py-1.5 rounded-lg text-[11px] text-white/30 hover:text-white/60 hover:bg-white/[0.04] w-full",
          variant === "compact" && "p-1.5 -mr-1 rounded-lg text-[10px] font-semibold text-white/25 hover:text-white/60 hover:bg-white/[0.04] uppercase tracking-wide",
          variant === "landing" && "px-3 py-1.5 rounded-lg text-sm text-white/60 hover:text-white",
        )}
      >
        <Globe className={variant === "compact" ? "w-3 h-3" : variant === "sidebar" ? "w-3 h-3" : "w-3.5 h-3.5"} />
        <span>{variant === "compact" ? currentCode : currentLabel}</span>
      </button>

      {open && (
        <div
          className={cn(
            "absolute z-50 min-w-[140px] rounded-xl border border-white/10 bg-[#141418] shadow-2xl shadow-black/60 py-1",
            variant === "sidebar" || variant === "compact" ? "bottom-full right-0 mb-1" : "top-full right-0 mt-1"
          )}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => switchLanguage(lang.code)}
              className={cn(
                "flex items-center justify-between w-full px-3 py-2 text-sm transition-colors",
                lang.code === currentLang
                  ? "text-vl-orange font-medium"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              )}
            >
              <span>{lang.label}</span>
              {lang.code === currentLang && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
